import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { 
  BarChart3, TrendingUp, Users, Calendar, DollarSign, FileText, RefreshCw,
  Star, CreditCard, UserCheck, Stethoscope, ClipboardList, Truck, Phone,
  Shield, Building, Settings, ChevronDown, ChevronRight, Search, Filter,
  FileSpreadsheet, Heart, Activity, Pill, AlertTriangle, Clock, MapPin
} from 'lucide-react';
import api from '../api';

const ReportsPage = () => {
  const [stats, setStats] = useState({
    patients: { total: 0, active: 0, newThisMonth: 0 },
    appointments: { total: 0, completed: 0, scheduled: 0 },
    billing: { total: 0, paid: 0, pending: 0 },
    documentation: { total: 0, completed: 0, pending: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(['favorites']);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [patientsRes, appointmentsRes, billingRes, docsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/appointments'),
        api.get('/billing'),
        api.get('/documentation')
      ]);

      const patients = patientsRes.data.success ? patientsRes.data.patients : [];
      const appointments = appointmentsRes.data.success ? appointmentsRes.data.appointments : [];
      const billing = billingRes.data.success ? billingRes.data.billing : [];
      const docs = docsRes.data.success ? docsRes.data.documentation : [];

      setStats({
        patients: {
          total: patients.length,
          active: patients.filter(p => p.status === 'Active').length,
          newThisMonth: patients.filter(p => {
            const created = new Date(p.createdAt);
            const now = new Date();
            return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
          }).length
        },
        appointments: {
          total: appointments.length,
          completed: appointments.filter(a => a.status === 'Completed').length,
          scheduled: appointments.filter(a => a.status === 'Scheduled').length
        },
        billing: {
          total: billing.reduce((sum, b) => sum + b.amount, 0),
          paid: billing.filter(b => b.status === 'Paid').reduce((sum, b) => sum + b.amount, 0),
          pending: billing.filter(b => b.status !== 'Paid').reduce((sum, b) => sum + b.amount, 0)
        },
        documentation: {
          total: docs.length,
          completed: docs.filter(d => d.status === 'Completed').length,
          pending: docs.filter(d => d.status !== 'Completed').length
        }
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive report categories matching Alora Plus
  const reportCategories = [
    {
      id: 'favorites',
      name: 'Favorites',
      icon: Star,
      color: 'yellow',
      reports: [
        { name: '485/POC Plus', href: '/dashboard/clinical/poc-485' },
        { name: 'Certification Due', href: '/dashboard/reports/certification-due' },
        { name: 'Signed 485 Received From Physician - Tracking', href: '/dashboard/reports/485-tracking' },
        { name: 'Signed Orders & Documents Received From Physician - Tracking', href: '/dashboard/reports/orders-tracking' },
        { name: 'Signed Face to Face Encounter Sheet Not Received From Physician - Tracking', href: '/dashboard/reports/f2f-tracking' },
        { name: 'Therapy/MSW POC', href: '/dashboard/reports/therapy-poc' },
        { name: 'CTI-Tracking', href: '/dashboard/reports/cti-tracking' }
      ]
    },
    {
      id: 'ar',
      name: 'A/R',
      icon: CreditCard,
      color: 'green',
      reports: [
        { name: 'Aging (Balance Due)', href: '/dashboard/reports/aging' },
        { name: 'Check Details', href: '/dashboard/reports/check-details' },
        { name: 'Payment Summary', href: '/dashboard/reports/payment-summary' },
        { name: 'Transaction (Charges, Payments, Adjustments & Balances)', href: '/dashboard/reports/transactions' },
        { name: 'Profit / Loss By Claim', href: '/dashboard/reports/profit-loss' },
        { name: 'Revenue by Visit', href: '/dashboard/reports/revenue-visit' },
        { name: 'Episodic Revenue Recognition', href: '/dashboard/reports/episodic-revenue' }
      ]
    },
    {
      id: 'billing',
      name: 'Billing',
      icon: DollarSign,
      color: 'blue',
      reports: [
        { name: 'Visits Completed but Not Billed', href: '/dashboard/reports/unbilled-visits' },
        { name: 'Claims ready for Electronic File creation', href: '/dashboard/reports/claims-ready' },
        { name: 'Claim Detail Report', href: '/dashboard/reports/claim-detail' },
        { name: 'PDGM/PPS Due Report', href: '/dashboard/reports/pdgm-due' },
        { name: 'Billing Code List', href: '/dashboard/reports/billing-codes' },
        { name: 'Medicare Pre-Claim Review Request', href: '/dashboard/reports/medicare-preclaim' },
        { name: 'PDGM LUPA Threshold Tracking', href: '/dashboard/reports/pdgm-lupa' },
        { name: 'Revenue by Referral Source', href: '/dashboard/reports/revenue-referral' },
        { name: 'Orphan Authorizations', href: '/dashboard/reports/orphan-auth' }
      ]
    },
    {
      id: 'caregiver',
      name: 'Caregiver',
      icon: UserCheck,
      color: 'purple',
      reports: [
        { name: 'Caregiver Availability', href: '/dashboard/reports/caregiver-availability' },
        { name: 'Caregiver Roster - Basic', href: '/dashboard/reports/caregiver-roster-basic' },
        { name: 'Caregiver Roster - Detail', href: '/dashboard/reports/caregiver-roster-detail' },
        { name: 'Credentials Due', href: '/dashboard/reports/credentials-due' },
        { name: 'Caregiver Communication Log', href: '/dashboard/reports/caregiver-comm-log' },
        { name: 'Caregiver Absences', href: '/dashboard/reports/caregiver-absences' },
        { name: 'Payroll Premium', href: '/dashboard/reports/payroll-premium' },
        { name: 'HHVBP Legacy', href: '/dashboard/reports/hhvbp-legacy' },
        { name: 'Payroll With Overtime', href: '/dashboard/reports/payroll-overtime' },
        { name: 'Incident Report Summary', href: '/dashboard/reports/incident-summary' }
      ]
    },
    {
      id: 'medicare-cost',
      name: 'Medicare Cost Reporting',
      icon: FileSpreadsheet,
      color: 'teal',
      reports: [
        { name: 'Medicare Cost Reporting', href: '/dashboard/reports/medicare-cost' }
      ]
    },
    {
      id: 'oasis',
      name: 'OASIS',
      icon: ClipboardList,
      color: 'indigo',
      reports: [
        { name: 'Print Blank Comprehensive Assessment', href: '/dashboard/reports/blank-assessment' },
        { name: 'OASIS Export Status', href: '/dashboard/tools/oasis-export' },
        { name: 'OASIS QA Status', href: '/dashboard/reports/oasis-qa-status' },
        { name: 'Transfer With No ROC', href: '/dashboard/reports/transfer-no-roc' }
      ]
    },
    {
      id: 'orders',
      name: 'Orders & Documents',
      icon: FileText,
      color: 'gray',
      reports: [
        { name: 'Signed Orders & Documents Received from Physician - Tracking', href: '/dashboard/reports/orders-received' },
        { name: 'Orders - Batch Print', href: '/dashboard/reports/orders-batch-print' }
      ]
    },
    {
      id: 'patient',
      name: 'Patient',
      icon: Users,
      color: 'blue',
      reports: [
        { name: 'Admission Detail (Census)', href: '/dashboard/reports/admission-census' },
        { name: 'Admission Profile (Face Sheet)', href: '/dashboard/reports/face-sheet' },
        { name: 'Aide POC Due', href: '/dashboard/reports/aide-poc-due' },
        { name: 'Communication Log', href: '/dashboard/patient/communication-log' },
        { name: 'Transfer to Inpatient Report', href: '/dashboard/reports/transfer-inpatient' },
        { name: 'Discharge Detail', href: '/dashboard/reports/discharge-detail' },
        { name: 'Discharge/Transfer Tracking', href: '/dashboard/reports/discharge-tracking' },
        { name: 'Prior Authorizations Due', href: '/dashboard/reports/prior-auth-due' },
        { name: 'Prior Authorization Utilization', href: '/dashboard/reports/prior-auth-util' },
        { name: 'Referral (from Patient Demographics)', href: '/dashboard/reports/referral-demographics' },
        { name: 'Referral Source (from Admission)', href: '/dashboard/reports/referral-admission' },
        { name: 'Statistical Report', href: '/dashboard/reports/statistical' },
        { name: 'Signed Face To Face Encounter Sheet Not Received - Tracking', href: '/dashboard/reports/f2f-not-received' },
        { name: 'Skilled Nursing Visit Note - Batch Print', href: '/dashboard/reports/sn-batch-print' },
        { name: 'Vitals History from Skilled Nursing Visit Note', href: '/dashboard/reports/vitals-history' },
        { name: 'Therapy Assessment/Reassessment Visits Due', href: '/dashboard/reports/therapy-visits-due' },
        { name: 'Patient Birthday List', href: '/dashboard/reports/birthday-list' },
        { name: 'Visits By Principal Diagnosis', href: '/dashboard/reports/visits-diagnosis' },
        { name: 'Aide Note Batch Print', href: '/dashboard/reports/aide-batch-print' },
        { name: 'Aide Supervisory Due Report', href: '/dashboard/reports/aide-supervisory-due' },
        { name: 'Supervisory Due Report', href: '/dashboard/reports/supervisory-due' },
        { name: 'Med Profile - Batch Print', href: '/dashboard/reports/med-profile-print' }
      ]
    },
    {
      id: 'physician',
      name: 'Physician',
      icon: Stethoscope,
      color: 'red',
      reports: [
        { name: 'Physician List', href: '/dashboard/setup/physician' }
      ]
    },
    {
      id: 'qa',
      name: 'QA',
      icon: Shield,
      color: 'orange',
      reports: [
        { name: '485 Status', href: '/dashboard/reports/485-status' },
        { name: 'MSW Visit Note Status', href: '/dashboard/reports/msw-status' },
        { name: 'NON-OASIS Status', href: '/dashboard/reports/non-oasis-status' },
        { name: 'OASIS QA Status', href: '/dashboard/reports/oasis-qa' },
        { name: 'OT Visit Note Status', href: '/dashboard/reports/ot-status' },
        { name: 'PT Visit Note Status', href: '/dashboard/reports/pt-status' },
        { name: 'Vitals History from Skilled Nursing Visit Note', href: '/dashboard/reports/vitals-sn' },
        { name: 'Incident Report Summary', href: '/dashboard/clinical/incident-report' }
      ]
    },
    {
      id: 'qapi',
      name: 'QAPI',
      icon: Activity,
      color: 'pink',
      reports: [
        { name: 'Infections', href: '/dashboard/reports/infections' },
        { name: 'Patient Falls', href: '/dashboard/reports/patient-falls' },
        { name: 'Incident Report Summary', href: '/dashboard/clinical/incident-report' }
      ]
    },
    {
      id: 'supply',
      name: 'Supply',
      icon: Truck,
      color: 'cyan',
      reports: [
        { name: 'Supply Code List', href: '/dashboard/setup/supply-code' },
        { name: 'Supply Log', href: '/dashboard/reports/supply-log' }
      ]
    },
    {
      id: 'visits',
      name: 'Visits/Scheduler',
      icon: Calendar,
      color: 'green',
      reports: [
        { name: 'Visit List', href: '/dashboard/scheduling' },
        { name: 'Calendar', href: '/dashboard/scheduling' },
        { name: 'Visit Summary by Discipline', href: '/dashboard/reports/visit-discipline' },
        { name: 'Missed Visit Detail', href: '/dashboard/reports/missed-visits' },
        { name: 'Patients and Visits by Age', href: '/dashboard/reports/visits-by-age' },
        { name: 'Patients with No Scheduled Visits', href: '/dashboard/reports/no-visits' },
        { name: 'Visits Document Status', href: '/dashboard/reports/visit-doc-status' },
        { name: 'Open Shift Post', href: '/dashboard/reports/open-shifts' },
        { name: 'Overlapping Visits', href: '/dashboard/reports/overlapping-visits' }
      ]
    },
    {
      id: 'telephony',
      name: 'Telephony',
      icon: Phone,
      color: 'violet',
      reports: [
        { name: 'Telephony Log', href: '/dashboard/reports/telephony-log' },
        { name: 'Telephony Tasks List', href: '/dashboard/reports/telephony-tasks' },
        { name: 'Telephony Tasks Done / Not Done', href: '/dashboard/reports/telephony-status' },
        { name: 'Telephony Information Card For Caregivers', href: '/dashboard/reports/telephony-card' }
      ]
    },
    {
      id: 'audit',
      name: 'Audit Trail',
      icon: Clock,
      color: 'slate',
      reports: [
        { name: 'Audit Trail', href: '/dashboard/tools/signature-audit' }
      ]
    },
    {
      id: 'california',
      name: 'California ALIRTS Reporting',
      icon: MapPin,
      color: 'amber',
      reports: [
        { name: 'California ALIRTS Reporting', href: '/dashboard/reports/california-alirts' }
      ]
    },
    {
      id: 'evv',
      name: 'CareConnect/EVV',
      icon: Settings,
      color: 'emerald',
      reports: [
        { name: 'CareConnect Visits', href: '/dashboard/reports/careconnect-visits' },
        { name: 'CareConnect Status List', href: '/dashboard/reports/careconnect-status' },
        { name: 'Aide Note Batch Print', href: '/dashboard/reports/aide-notes-print' }
      ]
    },
    {
      id: 'admin',
      name: 'Admin',
      icon: Building,
      color: 'gray',
      reports: [
        { name: 'Active Users List', href: '/dashboard/setup/user-management' },
        { name: 'Fax Usage', href: '/dashboard/reports/fax-usage' }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const expandAll = () => {
    setExpandedCategories(reportCategories.map(c => c.id));
  };

  const collapseAll = () => {
    setExpandedCategories([]);
  };

  const getColorClasses = (color) => {
    const colors = {
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-200', hover: 'hover:bg-yellow-50' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', hover: 'hover:bg-green-50' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-50' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', hover: 'hover:bg-purple-50' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200', hover: 'hover:bg-orange-50' },
      red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', hover: 'hover:bg-red-50' },
      indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:bg-indigo-50' },
      pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200', hover: 'hover:bg-pink-50' },
      teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200', hover: 'hover:bg-teal-50' },
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', hover: 'hover:bg-cyan-50' },
      violet: { bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200', hover: 'hover:bg-violet-50' },
      slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', hover: 'hover:bg-slate-50' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', hover: 'hover:bg-amber-50' },
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:bg-emerald-50' },
      gray: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', hover: 'hover:bg-gray-50' }
    };
    return colors[color] || colors.gray;
  };

  // Filter reports based on search query
  const filteredCategories = reportCategories.map(category => ({
    ...category,
    reports: category.reports.filter(report => 
      report.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.reports.length > 0 || searchQuery === '');

  const reportCards = [
    { 
      title: 'Patient Census Report', 
      icon: Users, 
      color: 'blue', 
      description: 'Track patient admissions, discharges, and census',
      stats: [
        { label: 'Total Patients', value: stats.patients.total },
        { label: 'Active', value: stats.patients.active },
        { label: 'New This Month', value: stats.patients.newThisMonth }
      ]
    },
    { 
      title: 'Visit Completion Report', 
      icon: Calendar, 
      color: 'green', 
      description: 'Monitor visit completion rates and scheduling',
      stats: [
        { label: 'Total Visits', value: stats.appointments.total },
        { label: 'Completed', value: stats.appointments.completed },
        { label: 'Scheduled', value: stats.appointments.scheduled }
      ]
    },
    { 
      title: 'Revenue Analysis', 
      icon: TrendingUp, 
      color: 'purple', 
      description: 'Detailed revenue breakdown by payer and service',
      stats: [
        { label: 'Total Revenue', value: `$${stats.billing.total.toLocaleString()}` },
        { label: 'Collected', value: `$${stats.billing.paid.toLocaleString()}` },
        { label: 'Pending', value: `$${stats.billing.pending.toLocaleString()}` }
      ]
    },
    { 
      title: 'Documentation Status', 
      icon: FileText, 
      color: 'orange', 
      description: 'Track clinical documentation completion',
      stats: [
        { label: 'Total Docs', value: stats.documentation.total },
        { label: 'Completed', value: stats.documentation.completed },
        { label: 'Pending', value: stats.documentation.pending }
      ]
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Reports & Analytics</h1>
            <p className="text-gray-600 mt-1">Insights and performance metrics</p>
          </div>
          <button 
            onClick={fetchReportData}
            className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-[#ea580c]" />
          </div>
        ) : (
          <>
            {/* Quick Stats Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {reportCards.map((report, index) => {
                const Icon = report.icon;
                const colorClasses = getColorClasses(report.color);
                return (
                  <div 
                    key={index} 
                    className={`bg-white p-4 rounded-xl shadow-sm border ${colorClasses.border} hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 ${colorClasses.bg} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${colorClasses.text}`} />
                      </div>
                      <h3 className="font-semibold text-sm text-gray-800">{report.title}</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {report.stats.map((stat, statIndex) => (
                        <div key={statIndex} className="text-center">
                          <p className={`text-lg font-bold ${colorClasses.text}`}>{stat.value}</p>
                          <p className="text-xs text-gray-500">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Report Categories - Alora Plus Style */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* Search and Controls */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Filter reports..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
                      data-testid="report-search"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={expandAll}
                      className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300"
                    >
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300"
                    >
                      Collapse All
                    </button>
                  </div>
                </div>
              </div>

              {/* Report Categories List */}
              <div className="divide-y divide-gray-100">
                {filteredCategories.map((category) => {
                  const Icon = category.icon;
                  const colorClasses = getColorClasses(category.color);
                  const isExpanded = expandedCategories.includes(category.id);
                  
                  return (
                    <div key={category.id} data-testid={`report-category-${category.id}`}>
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className={`w-full px-4 py-3 flex items-center justify-between ${colorClasses.hover} transition-colors`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${colorClasses.bg} rounded-lg flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${colorClasses.text}`} />
                          </div>
                          <span className="font-medium text-gray-800">{category.name}</span>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                            {category.reports.length}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      
                      {/* Report Links */}
                      {isExpanded && (
                        <div className="bg-gray-50 px-4 py-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                            {category.reports.map((report, index) => (
                              <a
                                key={index}
                                href={report.href}
                                className="px-3 py-2 text-sm text-gray-700 hover:text-[#f39c12] hover:bg-white rounded-lg transition-colors flex items-center gap-2"
                                data-testid={`report-link-${category.id}-${index}`}
                              >
                                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                                {report.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Performance Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Performance Overview</h2>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-blue-600">{stats.patients.active}</p>
                  <p className="text-sm text-gray-600">Active Patients</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-green-600">{stats.appointments.scheduled}</p>
                  <p className="text-sm text-gray-600">Scheduled Visits</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <DollarSign className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-purple-600">${stats.billing.paid.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Revenue Collected</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <FileText className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-orange-600">
                    {stats.documentation.total > 0 
                      ? Math.round((stats.documentation.completed / stats.documentation.total) * 100)
                      : 0}%
                  </p>
                  <p className="text-sm text-gray-600">Doc Completion</p>
                </div>
              </div>
            </div>

            {/* Activity Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Activity Summary</h2>
              <div className="h-64 bg-gradient-to-t from-gray-50 to-white rounded-lg flex items-end justify-around p-4">
                {[
                  { label: 'Patients', value: stats.patients.total, color: 'bg-blue-500' },
                  { label: 'Active', value: stats.patients.active, color: 'bg-blue-400' },
                  { label: 'Visits', value: stats.appointments.total, color: 'bg-green-500' },
                  { label: 'Completed', value: stats.appointments.completed, color: 'bg-green-400' },
                  { label: 'Docs', value: stats.documentation.total, color: 'bg-orange-500' },
                  { label: 'Done', value: stats.documentation.completed, color: 'bg-orange-400' }
                ].map((item, index) => {
                  const maxVal = Math.max(
                    stats.patients.total, 
                    stats.appointments.total, 
                    stats.documentation.total, 
                    1
                  );
                  const heightPercent = Math.max((item.value / maxVal) * 80, 5);
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <span className="text-sm font-semibold text-gray-700 mb-2">{item.value}</span>
                      <div 
                        className={`w-12 ${item.color} rounded-t transition-all duration-500`} 
                        style={{ height: `${heightPercent}%` }}
                      ></div>
                      <span className="text-xs text-gray-600 mt-2">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
